/**
 * Elite Realty 01 — Layered listing sheet.
 *
 * The density reference for the pack. A full-bleed exterior washed into navy,
 * a rotated gold stamp claiming the listing, and a bone card lifted OVER the
 * photograph's lower edge carrying the headline and the figures — the overlap
 * is what gives a flat sheet depth. Beneath it, a dot field fills what would
 * otherwise be dead navy, a pair of overlapping interior photographs sit on
 * their own cards, and the agent closes the page on a lifted plate.
 *
 * Corner brackets hold the trim instead of a full frame.
 */

import { CANVAS, SPACE, RULE, RADIUS, SHADOW } from '../system/tokens';
import { getPalette, scrim } from '../system/palettes';
import { roles } from '../system/typography';
import { grid } from '../system/grid';
import { text, shape, rule, image, page } from '../system/elements';
import { getIcon } from '../system/icons';
import { PHOTOS } from '../system/photos';
import { BRAND, SPECS, FEATURES } from './system';
import { cornerBrackets, wash, stamp, card, iconDisc, dotGrid, layeredPhotos } from './decor';

const ID = 'tpl-elite-flyer-01';

export default {
  id: ID,
  meta: {
    name: 'Elite — Just Listed',
    collection: 'elite-realty',
    archetype: 'layered-card-over-hero',
    documentType: 'Flyer',
    industry: 'Real Estate',
    subCategory: 'Real Estate',
    canvas: CANVAS.usLetterPortrait,
    palette: 'elite-navy-gold',
    pairing: 'elite-signature',
    tags: ['real estate', 'flyer', 'just listed', 'luxury', 'layered', 'premium', 'navy', 'us letter'],
    description: 'A washed full-bleed exterior with a rotated gold stamp, a bone card lifted over the photograph carrying the headline and figures, and layered interior photographs beneath.',
  },

  build() {
    const canvas = CANVAS.usLetterPortrait;
    const p = getPalette('elite-navy-gold');
    const r = roles('elite-signature');
    const g = grid(canvas, { margin: 160 });
    const els = [];

    const HERO_H = 1400;
    const CARD_Y = 1180;
    const CARD_H = 700;

    // ── Hero, washed into the field beneath it ──────────────────────────────
    els.push(image(`${ID}-hero`, { x: 0, y: 0, width: canvas.width, height: HERO_H, src: PHOTOS.timberDusk }));
    els.push(wash(`${ID}-wash`, { x: 0, y: HERO_H - 620, width: canvas.width, height: 620, color: p.bg, to: 0.98 }));
    els.push(wash(`${ID}-wash-top`, { x: 0, y: 0, width: canvas.width, height: 420, color: p.bg, angle: 0, from: 0, to: 0.72 }));

    // ── Brandmark ───────────────────────────────────────────────────────────
    els.push(...iconDisc(`${ID}-logo`, {
      x: g.left, y: 130, size: 104, content: getIcon('roofMark'), discFill: p.accent, iconColor: p.accentInk,
    }));
    els.push(text(`${ID}-logo-name`, r.h3, BRAND.name, { x: g.left + 132, y: 132, width: 700 },
      { color: p.ink, fontSize: 52, letterSpacing: 16 }));
    els.push(text(`${ID}-logo-sub`, r.statLabel, BRAND.sub, { x: g.left + 132, y: 208, width: 700 },
      { color: p.accent, fontSize: 24, letterSpacing: 10 }));

    // ── The claim, stamped over the top-right of the photograph ─────────────
    els.push(...stamp(`${ID}`, r, {
      x: 1930, y: 150, size: 420, fill: p.accent, inkColor: p.accentInk, rotation: -12,
      lines: [
        { copy: 'JUST', size: 78, tracking: 4 },
        { copy: 'LISTED', size: 78, tracking: 4 },
        { copy: 'AUSTIN · TX', size: 26, tracking: 6 },
      ],
    }));

    // ── The card, lifted over the photograph's lower edge ───────────────────
    els.push(...card(`${ID}-plate`, {
      x: g.left, y: CARD_Y, width: g.contentWidth, height: CARD_H,
      fill: p.surface, radius: RADIUS.md, accentColor: p.accent, accentWidth: RULE.thick,
    }));

    let y = CARD_Y + SPACE.lg;
    const inX = g.left + SPACE.lg + RULE.thick;
    const inW = g.contentWidth - SPACE.lg * 2 - RULE.thick;

    els.push(text(`${ID}-eyebrow`, r.statLabel, 'Offered at $789,000', { x: inX, y, width: inW },
      { color: p.accentDeep, fontSize: 34 }));
    y += 62;

    els.push(text(`${ID}-script`, r.script, 'Modern home', { x: inX + 8, y, width: inW },
      { color: p.accentDeep, fontSize: 104, overlapOk: true }));
    y += 104 * 0.74;

    const headline = text(`${ID}-headline`, r.display, 'FOR SALE', { x: inX, y, width: inW },
      { color: p.surfaceInk, fontSize: 158 });
    els.push(headline);
    y += headline.height + SPACE.xs;

    els.push(text(`${ID}-addr`, r.body, `${BRAND.address[0]}, ${BRAND.address[1]}`, { x: inX, y, width: inW },
      { color: p.surfaceInkMuted, fontSize: 40 }));
    y += 40 * 1.5 + SPACE.md;

    // Figures, each on its own disc.
    const gap = SPACE.md;
    const cw = Math.floor((inW - gap * (SPECS.length - 1)) / SPECS.length);
    SPECS.forEach((s, i) => {
      const cx = inX + i * (cw + gap);
      els.push(...iconDisc(`${ID}-spec${i}`, {
        x: cx, y, size: 78, content: getIcon(s.icon),
        discFill: p.bg, iconColor: p.accent, ringColor: scrim(p.surfaceInk, 0.3),
      }));
      els.push(text(`${ID}-spec${i}-value`, r.h3, s.value, { x: cx + 100, y: y + 2, width: cw - 100 },
        { color: p.surfaceInk, fontSize: 50 }));
      els.push(text(`${ID}-spec${i}-label`, r.statLabel, s.label, { x: cx + 100, y: y + 72, width: cw - 100 },
        { color: p.surfaceInkMuted, fontSize: 24 }));
    });

    // ── Beneath the card: texture, features, layered interiors ─────────────
    els.push(dotGrid(`${ID}-dots`, {
      x: 1420, y: 2640, width: 980, height: 130, color: scrim(p.ink, 0.34), step: 30, radius: 5,
    }));

    let fy = 1990;
    els.push(text(`${ID}-feat-head`, r.h3, 'Property Features', { x: g.left, y: fy, width: 1120 },
      { color: p.accent, fontSize: 56, letterSpacing: 3 }));
    fy += 76;
    els.push(rule(`${ID}-feat-rule`, { x: g.left, y: fy, width: 220, weight: RULE.medium, color: p.accent }));
    fy += RULE.medium + SPACE.md;

    FEATURES.forEach((f, i) => {
      const ry = fy + i * 78;
      els.push(shape(`${ID}-feat${i}-dot`, {
        x: g.left, y: ry + 16, width: 16, height: 16, kind: 'circle', fill: p.accent,
      }));
      els.push(text(`${ID}-feat${i}`, r.body, f, { x: g.left + 44, y: ry, width: 1076 },
        { color: p.ink, fontSize: 36 }));
    });

    els.push(...layeredPhotos(`${ID}-gallery`, {
      x: 1420, y: 1970, width: 980, height: 640,
      main: PHOTOS.openPlanLiving, inset: PHOTOS.brassBedroom,
      cardColor: p.surface, accentColor: p.accent, bound: 2390,
    }));

    // ── Agent, on a lifted plate ────────────────────────────────────────────
    const AG_Y = 2790;
    const AG_H = 300;
    els.push(...card(`${ID}-agent-plate`, {
      x: g.left, y: AG_Y, width: g.contentWidth, height: AG_H,
      fill: p.surface, radius: RADIUS.md, shadow: SHADOW.lift,
    }));

    const PORTRAIT = 170;
    els.push(shape(`${ID}-agent-ring`, {
      x: g.left + SPACE.md - 5, y: AG_Y + (AG_H - PORTRAIT) / 2 - 5,
      width: PORTRAIT + 10, height: PORTRAIT + 10, kind: 'circle', fill: p.accent,
    }));
    els.push(image(`${ID}-agent-photo`, {
      x: g.left + SPACE.md, y: AG_Y + (AG_H - PORTRAIT) / 2,
      width: PORTRAIT, height: PORTRAIT, src: PHOTOS.agentHeadshot, cornerRadius: PORTRAIT / 2,
    }));

    const aX = g.left + SPACE.md + PORTRAIT + SPACE.md;
    els.push(text(`${ID}-agent-name`, r.h3, BRAND.agent, { x: aX, y: AG_Y + 62, width: 900 },
      { color: p.surfaceInk, fontSize: 58, letterSpacing: 3 }));
    els.push(text(`${ID}-agent-title`, r.statLabel, BRAND.agentTitle, { x: aX, y: AG_Y + 148, width: 900 },
      { color: p.accentDeep, fontSize: 28 }));
    els.push(text(`${ID}-agent-contact`, r.caption, `${BRAND.phone}\n${BRAND.web}`, {
      x: g.right - 720, y: AG_Y + 82, width: 700, align: 'right',
    }, { color: p.surfaceInkMuted, fontSize: 34 }));

    // ── Brackets hold the trim ──────────────────────────────────────────────
    els.push(...cornerBrackets(`${ID}`, {
      x: 90, y: 90, width: canvas.width - 180, height: canvas.height - 180,
      arm: 150, weight: RULE.medium, color: scrim(p.accent, 0.85),
    }));

    return {
      canvas,
      pages: [page(`${ID}-page-1`, { name: 'Flyer', background: p.bg, elements: els })],
    };
  },
};
